/* ============================================================================
   LA BARRA DE SINCRONIZACIÓN Y EL DIÁLOGO DEL CONFLICTO

   ═══ SIN ESTO, LA SINCRONIZACIÓN ES INVISIBLE ═══

   El motor podía estar guardando cambios en una cola que nunca subía y la
   pantalla decía lo mismo que si todo estuviera al día. El piloto se enteraba
   al día siguiente, cuando el asiento no estaba en el otro equipo.

   La barra dice tres cosas distintas, y no las mezcla:

     · **cuántos cambios esperan** (y si hay conexión);
     · **si el reloj del equipo está mal**, que es el riesgo real de decidir
       por marcas de tiempo;
     · **si hay un conflicto**, que no se resuelve solo: lo decide el piloto.

   ═══ EL DIÁLOGO NO ES UN ALERT ═══

   Un `window.confirm` no puede mostrar las dos versiones. El piloto tiene que
   ver qué decía la suya y qué dice la del servidor para elegir con la razón que
   tuvo el operador: la marca dice cuándo se escribió, no qué pasó.
   ========================================================================== */

import React, { useEffect, useState } from "react";

import { etiquetaDeEntidad } from "../services/sync/entidades.js";
import { TOLERANCIA_RELOJ_MS } from "../services/sync/motor.js";
import { useSincronizacion } from "../services/sync/useSincronizacion.js";
import "./sincronizacion.css";

const CAMPOS = {
    bitacora: ["fecha", "nVuelo", "tipoOperacion", "horasAeronave"],
    clientes: ["nombre", "contacto", "telefono"],
    aeronaves: ["matricula", "tipo", "serie"],
    pilotos: ["nombre", "apellido", "licencia"],
    eventosSgs: ["fecha", "descripcion", "ref"],
    auditorias: ["fecha", "tipo", "observacion"],
    noConformidades: ["fecha", "descripcion", "prioridad"],
};

function resumenDe(entidad, datos) {
    if (!datos || typeof datos !== "object") return "Registro sin datos.";
    const partes = (CAMPOS[entidad] || [])
        .map((campo) => datos[campo])
        .filter((valor) => valor !== undefined && valor !== null && String(valor).trim() !== "")
        .map((valor) => String(valor));
    if (partes.length) return partes.join(" · ");
    return datos.id ? `Registro ${datos.id}` : "Registro sin descripción.";
}

function fechaDe(ms) {
    if (!ms) return "sin fecha";
    try {
        return new Date(ms).toLocaleString("es", { dateStyle: "short", timeStyle: "short" });
    } catch {
        return "sin fecha";
    }
}

function minutosDe(ms) {
    return Math.max(1, Math.round(Math.abs(Number(ms) || 0) / 60000));
}

function textoDeEstado(estado) {
    const pendientes = Number(estado.pendientes) || 0;
    const conflictos = Number(estado.conflictos) || 0;
    const cambios = (n) => `${n} cambio${n === 1 ? "" : "s"}`;

    switch (estado.fase) {
        case "sincronizando":
            return "Sincronizando…";
        case "al-dia":
            return "Todo sincronizado.";
        case "pendiente":
            return `${cambios(pendientes)} guardado${pendientes === 1 ? "" : "s"} en este equipo, sin subir.`;
        case "conflicto":
            return `${cambios(conflictos)} en conflicto. Hay que decidir cuál vale.`;
        case "sin-servidor":
            return pendientes
                ? `Sin conexión con el servidor. ${cambios(pendientes)} guardado${pendientes === 1 ? "" : "s"} en este equipo.`
                : "Sin conexión con el servidor. Lo que registres queda guardado en este equipo.";
        case "error":
            return "No se pudo sincronizar. Lo que registres queda guardado en este equipo.";
        default:
            return "Preparando la sincronización…";
    }
}

/* ═══ LA VISTA, SEPARADA DEL MOTOR ═══

   Así se puede probar el texto, el aviso del reloj y el diálogo sin IndexedDB
   ni red. La que se conecta es la de abajo. */

export function BarraDeSincronizacionVista({
    estado,
    conflicto,
    rechazos = [],
    resolviendo = false,
    alSincronizar = () => {},
    alResolver = () => {},
}) {
    const [oculto, setOculto] = useState(null);
    const desfase = Number(estado?.desfaseMs) || 0;
    const relojMal = Math.abs(desfase) > TOLERANCIA_RELOJ_MS;
    const mostrarConflicto = Boolean(conflicto) && conflicto.id !== oculto;
    const pendientes = Number(estado?.pendientes) || 0;

    useEffect(() => {
        if (!mostrarConflicto) return undefined;
        const alTeclear = (evento) => {
            if (evento.key === "Escape") setOculto(conflicto.id);
        };
        window.addEventListener("keydown", alTeclear);
        return () => window.removeEventListener("keydown", alTeclear);
    }, [mostrarConflicto, conflicto?.id]);

    return (
        <>
            <div className={`sync-bar sync-bar--${estado?.fase || "inactivo"}`} role="status" aria-live="polite">
                <span className="sync-bar__punto" aria-hidden="true" />
                <span className="sync-bar__texto">{textoDeEstado(estado || {})}</span>

                {estado?.fase === "sincronizando" ? null : (
                    <button
                        type="button"
                        className="sync-bar__accion"
                        onClick={alSincronizar}
                        title="Intentar ahora"
                    >
                        Sincronizar
                    </button>
                )}

                {conflicto && (
                    <button
                        type="button"
                        className="sync-bar__accion sync-bar__accion--conflicto"
                        onClick={() => setOculto(null)}
                    >
                        Revisar conflicto
                    </button>
                )}
            </div>

            {relojMal && (
                <div className="sync-reloj" role="alert">
                    <strong>El reloj de este equipo está {desfase > 0 ? "atrasado" : "adelantado"} {minutosDe(desfase)} minutos.</strong>{" "}
                    La hora decide qué edición gana al sincronizar. Corrígelo antes de seguir registrando
                    vuelos: uno adelantado pisa las ediciones de los demás equipos.
                </div>
            )}

            {rechazos.length > 0 && (
                <details className="sync-rechazos">
                    <summary>
                        {rechazos.length} cambio{rechazos.length === 1 ? "" : "s"} no se pudo enviar
                    </summary>
                    <ul>
                        {rechazos.slice(0, 20).map((r) => (
                            <li key={`${r.entidad}:${r.id}`}>
                                <strong>{etiquetaDeEntidad(r.entidad)}</strong> · {r.motivo}
                            </li>
                        ))}
                    </ul>
                    {rechazos.length > 20 && <p>Y {rechazos.length - 20} más.</p>}
                </details>
            )}

            {mostrarConflicto && (
                <div className="sync-modal" role="dialog" aria-modal="true" aria-labelledby="sync-conflicto-titulo">
                    <div className="sync-modal__caja">
                        <h2 id="sync-conflicto-titulo" className="sync-modal__titulo">
                            Dos versiones del mismo registro
                        </h2>
                        <p className="sync-modal__nota">
                            Este equipo y otro editaron {etiquetaDeEntidad(conflicto.entidad).toLowerCase()} al mismo
                            tiempo. Elegí cuál vale: la que elijas se sincroniza en todos los equipos.
                        </p>

                        <div className="sync-versiones">
                            <section className="sync-version">
                                <h3>Tu versión</h3>
                                <p>
                                    {conflicto.propio?.borrado_en != null
                                        ? "Diste de baja este registro."
                                        : resumenDe(conflicto.entidad, conflicto.propio?.datos)}
                                </p>
                                <p className="sync-version__fecha">
                                    Guardada {fechaDe(conflicto.propio?.actualizado_en)}
                                </p>
                            </section>

                            <section className="sync-version sync-version--servidor">
                                <h3>La del servidor</h3>
                                <p>
                                    {conflicto.servidor?.borrado_en != null
                                        ? "El servidor dio de baja este registro."
                                        : resumenDe(conflicto.entidad, conflicto.servidor?.datos)}
                                </p>
                                <p className="sync-version__fecha">
                                    Guardada {fechaDe(conflicto.servidor?.actualizado_en)}
                                </p>
                            </section>
                        </div>

                        <div className="sync-modal__acciones">
                            <button
                                type="button"
                                className="sync-boton sync-boton--principal"
                                autoFocus
                                disabled={resolviendo}
                                onClick={() => alResolver("mia")}
                            >
                                Quedarme con la mía
                            </button>
                            <button
                                type="button"
                                className="sync-boton"
                                disabled={resolviendo}
                                onClick={() => alResolver("servidor")}
                            >
                                {conflicto.servidor?.borrado_en != null ? "Aceptar la baja" : "Usar la del servidor"}
                            </button>
                            <button
                                type="button"
                                className="sync-boton sync-boton--tenue"
                                disabled={resolviendo}
                                onClick={() => setOculto(conflicto.id)}
                            >
                                Decidir después
                            </button>
                        </div>

                        <p className="sync-modal__pie">
                            {pendientes > 0
                                ? `Mientras decidas, ${pendientes} cambio${pendientes === 1 ? "" : "s"} espera${pendientes === 1 ? "" : "n"} en este equipo.`
                                : "El resto de los datos sigue sincronizándose."}
                        </p>
                    </div>
                </div>
            )}
        </>
    );
}

export default function BarraDeSincronizacion({ activa = true, perfil = null }) {
    const { estado, conflicto, rechazos, resolviendo, sincronizar, resolver } = useSincronizacion({
        activa,
        perfil,
    });

    if (!activa) return null;

    return (
        <BarraDeSincronizacionVista
            estado={estado}
            conflicto={conflicto}
            rechazos={rechazos}
            resolviendo={resolviendo}
            alSincronizar={sincronizar}
            alResolver={resolver}
        />
    );
}
